import { themeVar } from '@/styles/theme';
import React from 'react';
import styled from 'styled-components';

import { AiAssistantIcon } from '@/assets/static/icons/ai-assistant';
import { allSidebarTabs } from '@/shared/constants/sidebarTabs';
import { useLocation, useNavigate } from 'react-router-dom';
import { useAuth } from '../../hooks/use-auth';
import { useResponsive } from '../../hooks/use-responsive';
import Button from '../basicComponents/button';

interface HeaderProps {
  modalControl?: JSX.Element;
  specialControl?: JSX.Element;
  title?: string;
  aiButtonConfig?: {
    isLoading?: boolean;
    secondaryIcon?: React.ReactNode;
    onClick: () => void;
  };
}

const Header = ({ specialControl, modalControl, title, aiButtonConfig }: HeaderProps) => {
  const { isAuthenticated } = useAuth();
  const navigate = useNavigate();

  const { pathname } = useLocation();
  const selectedTabIndex = allSidebarTabs.findIndex((tab) => pathname.includes(tab.path.split('/')[0])) ?? 0;
  const { isMobile, isDesktop, isTablet, isLargeDesktop } = useResponsive();

  const styledImage = allSidebarTabs[selectedTabIndex]?.icon
    ? React.cloneElement(allSidebarTabs[selectedTabIndex].icon, {
        fill: themeVar('font.strong'),
        width: isMobile ? 33 : 40,
        height: isMobile ? 33 : 40
      })
    : null;

  return (
    <HeaderWrap modalControl={!!modalControl} isMobile={isMobile} isTablet={isTablet}>
      {modalControl ? (
        modalControl
      ) : (
        <HeaderTitleWrap>
          <HeaderIconWrap>
            {styledImage}
            <HeaderTitle isTablet={isTablet} isMobile={isMobile}>
              {title ? title : allSidebarTabs[selectedTabIndex].label}
            </HeaderTitle>
          </HeaderIconWrap>
          {allSidebarTabs[selectedTabIndex]?.subTitle && !isMobile && <HeaderSubTitle>{allSidebarTabs[selectedTabIndex].subTitle}</HeaderSubTitle>}
        </HeaderTitleWrap>
      )}
      <SpecialControlWrap>
        {(isDesktop || isLargeDesktop || isTablet) && specialControl}
        {aiButtonConfig && (
          <Button
            onClick={aiButtonConfig.onClick}
            size="md"
            icon={<AiAssistantIcon width={23} height={23} fill={themeVar('context.light')} />}
            secondaryIcon={aiButtonConfig?.secondaryIcon}
            isLoading={aiButtonConfig?.isLoading}
            loaderColor={themeVar('context.light')}
          >
            AI Assistant
          </Button>
        )}
        {isAuthenticated && (isDesktop || isLargeDesktop) && (
          <HeaderAccountWrap onClick={() => navigate('/profile')}>
            <svg xmlns="http://www.w3.org/2000/svg" width="17" height="20" viewBox="0 0 17 20" fill="none">
              <path
                d="M16.9956 16.8776C17.0185 17.2984 16.9522 17.7193 16.8008 18.1129C16.6495 18.5064 16.4165 18.8638 16.1171 19.1617C15.477 19.737 14.6344 20.037 13.773 19.9963H3.23181C2.37042 20.037 1.52781 19.737 0.887645 19.1617C0.587355 18.8642 0.353464 18.507 0.201234 18.1135C0.0490033 17.7199 -0.0181465 17.2987 0.0041902 16.8776C0.0041902 16.4528 0.00420368 16.0429 0.0493801 15.6381C0.0803913 15.197 0.137376 14.7581 0.220045 14.3237C0.295991 13.8823 0.401628 13.4465 0.536267 13.0192C0.661942 12.6129 0.830188 12.2209 1.03823 11.8497C1.23594 11.4811 1.48806 11.1442 1.78616 10.8501C2.08651 10.5729 2.43755 10.3557 2.8202 10.2104C3.24928 10.0462 3.70581 9.96477 4.16545 9.97048C4.34818 10.0237 4.5187 10.112 4.66741 10.2304L5.56592 10.8052C5.97521 11.0501 6.41364 11.2432 6.87102 11.3799C7.91875 11.7263 9.05089 11.7263 10.0986 11.3799C10.5544 11.2432 10.9912 11.0502 11.3987 10.8052L12.3022 10.2304C12.4503 10.1111 12.6211 10.0227 12.8042 9.97048C13.2638 9.96477 13.7204 10.0462 14.1494 10.2104C14.5321 10.3557 14.8831 10.5729 15.1835 10.8501C15.4816 11.1442 15.7337 11.4811 15.9314 11.8497C16.1396 12.2209 16.3079 12.6129 16.4334 13.0192C16.5698 13.4463 16.6771 13.8821 16.7546 14.3237C16.8348 14.7585 16.8918 15.1973 16.9253 15.6381C16.9855 16.0429 16.9956 16.4528 16.9956 16.8776ZM11.5493 1.38401C12.3644 2.31078 12.8137 3.50077 12.8137 4.73266C12.8137 5.96454 12.3644 7.15448 11.5493 8.08124C11.169 8.51585 10.6997 8.86429 10.1728 9.10304C9.64591 9.34179 9.07374 9.46531 8.49486 9.46531C7.91597 9.46531 7.34382 9.34179 6.81693 9.10304C6.29004 8.86429 5.82067 8.51585 5.44043 8.08124C4.62234 7.15591 4.17104 5.96543 4.17104 4.73266C4.17104 3.49988 4.62234 2.30934 5.44043 1.38401C5.82067 0.949399 6.29004 0.601027 6.81693 0.362277C7.34382 0.123526 7.91597 0 8.49486 0C9.07374 0 9.64591 0.123526 10.1728 0.362277C10.6997 0.601027 11.169 0.949399 11.5493 1.38401Z"
                fill={themeVar('layer.3')}
              />
            </svg>
          </HeaderAccountWrap>
        )}
      </SpecialControlWrap>
    </HeaderWrap>
  );
};

const HeaderAccountWrap = styled.div`
  display: flex;
  align-items: center;
  padding: 8px 24px;
  height: 45px;
  border-radius: 4px;
  cursor: pointer;
  border: 1px solid ${themeVar('border.base')};
  background: ${themeVar('layer.base')};
`;

const SpecialControlWrap = styled.div`
  display: flex;
  align-items: center;
  gap: 30px;
`;

export const HeaderSubTitle = styled.div`
  display: flex;
  font-family: Blinker, serif;
  font-size: 13px;
  line-height: 18px;
  font-weight: 400;
  color: ${themeVar('font.weak')};
  padding: 4px 0;
`;

export const HeaderTitle = styled.div<{ isMobile: boolean; isTablet: boolean }>`
  font-family: Blinker, serif;
  font-size: ${({ isMobile, isTablet }) => (isTablet ? '24px' : isMobile ? '20px' : '33px')};
  line-height: 33px;
  font-weight: 700;
  color: ${themeVar('font.strong')};
`;

export const HeaderIconWrap = styled.div`
  display: flex;
  align-items: center;
  gap: 10px;
`;

export const HeaderTitleWrap = styled.div`
  display: flex;
  align-items: flex-end;
  gap: 20px;
`;

const HeaderWrap = styled.div<{ isMobile: boolean; isTablet: boolean; modalControl: boolean }>`
  display: flex;
  margin: ${({ isMobile }) => (isMobile ? '0' : '0 -24px')};
  justify-content: space-between;
  align-items: center;
  // width: ${({ isMobile, modalControl }) => (modalControl && isMobile ? 'calc(100% + 44px)' : 'calc(100% + 48px)')};
  width: ${({ isMobile, modalControl }) => (modalControl && isMobile ? '100%' : 'calc(100% + 48px)')};
  top: ${({ isMobile, isTablet }) => (isMobile || isTablet ? '50px' : 0)};
  position: sticky;
  height: 60px;
  background: ${themeVar('layer.12')};
  z-index: 101;
  padding: ${({ isMobile }) => (isMobile ? '0 30px' : '0px 50px')};
  box-shadow:
    0 12px 16px rgba(0, 0, 0, 0.04),
    0 2px 8px rgba(0, 0, 0, 0.03),
    0 1.12694px 2.75474px rgba(0, 0, 0, 0.01);
  transition: 0.3s;
`;

export default Header;
